import { handleAxiosError, type ZenseiApiErrorBody } from "@/api/utils";
import type {
  CreateFlowScheduleRequest,
  DuplicateFlowScheduleRequest,
  FlowSchedule,
  FlowScheduleRun,
  ListFlowScheduleRunsResponse,
  UpdateFlowScheduleRequest,
} from "@cohesive/api-client";
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { orgAPI } from "./api";

/**
 * Server `UserMessage` for a failed flow-schedule call, falling back to the
 * given copy. The cron guardrails (e.g. "Automation schedules must be at
 * least 30 minutes apart.") arrive this way and are worth showing verbatim.
 */
export function flowScheduleErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as ZenseiApiErrorBody | undefined;
    const message = body?.error?.message;
    if (message) return message;
  }
  return fallback;
}

export type {
  FlowSchedule,
  FlowScheduleRun,
  UpdateFlowScheduleRequest,
} from "@cohesive/api-client";

const RUNS_PAGE_SIZE = 20;
/** Poll cadence for the runs list while a run is in flight. */
const RUNS_ACTIVE_POLL_MS = 10_000;

/** The caller's own automations (the unfiltered list endpoint). */
export function useFlowSchedules(
  options?: Partial<UseQueryOptions<FlowSchedule[], AxiosError>>,
) {
  return useQuery<FlowSchedule[], AxiosError>({
    queryKey: ["flow-schedules"],
    queryFn: async () => {
      return await orgAPI.flowSchedules.list();
    },
    ...options,
  });
}

export function useFlowSchedule(
  flowScheduleId: string,
  options?: Partial<UseQueryOptions<FlowSchedule, AxiosError>>,
) {
  return useQuery<FlowSchedule, AxiosError>({
    queryKey: ["flow-schedule", flowScheduleId],
    queryFn: async () => {
      return await orgAPI.flowSchedules.getById(flowScheduleId);
    },
    enabled: !!flowScheduleId,
    ...options,
  });
}

export function useCreateFlowSchedule(
  options?: UseMutationOptions<
    FlowSchedule,
    AxiosError,
    CreateFlowScheduleRequest
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<FlowSchedule, AxiosError, CreateFlowScheduleRequest>({
    mutationFn: async (data) => {
      return await orgAPI.flowSchedules.create(data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["flow-schedules"] });
      queryClient.setQueryData(["flow-schedule", data.id], data);
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useUpdateFlowSchedule(
  options?: UseMutationOptions<
    FlowSchedule,
    AxiosError,
    { id: string; data: UpdateFlowScheduleRequest }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    FlowSchedule,
    AxiosError,
    { id: string; data: UpdateFlowScheduleRequest }
  >({
    mutationFn: async ({ id, data }) => {
      return await orgAPI.flowSchedules.update(id, data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      // The PATCH response is the updated object — seed the detail cache with
      // it so pause/resume settles without a refetch flash.
      queryClient.setQueryData(["flow-schedule", variables.id], data);
      queryClient.invalidateQueries({ queryKey: ["flow-schedules"] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

/** Hard delete — automations have no trash. */
export function useDeleteFlowSchedule(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: async (flowScheduleId) => {
      try {
        await orgAPI.flowSchedules.delete(flowScheduleId);
      } catch (e) {
        return handleAxiosError(e);
      }
    },
    ...options,
    onSuccess: (_, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["flow-schedules"] });
      queryClient.removeQueries({ queryKey: ["flow-schedule", variables] });
      queryClient.removeQueries({
        queryKey: ["flow-schedule-runs", variables],
      });
      if (options?.onSuccess) {
        options.onSuccess(_, variables, context);
      }
    },
  });
}

export function useDuplicateFlowSchedule(
  options?: UseMutationOptions<
    FlowSchedule,
    AxiosError,
    { id: string; data: DuplicateFlowScheduleRequest }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    FlowSchedule,
    AxiosError,
    { id: string; data: DuplicateFlowScheduleRequest }
  >({
    mutationFn: async ({ id, data }) => {
      return await orgAPI.flowSchedules.duplicate(id, data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["flow-schedules"] });
      queryClient.setQueryData(["flow-schedule", data.id], data);
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

/**
 * Runs (newest first, cursor-paginated). Polls while page one holds a running
 * run so status flips settle without a manual refresh; run status is volatile,
 * so the cache opts out of localStorage persistence.
 */
export function useFlowScheduleRuns(flowScheduleId: string) {
  return useInfiniteQuery({
    queryKey: ["flow-schedule-runs", flowScheduleId],
    queryFn: async ({
      pageParam,
    }: {
      pageParam?: string;
    }): Promise<ListFlowScheduleRunsResponse> => {
      return await orgAPI.flowSchedules.listRuns(flowScheduleId, {
        before_ts: pageParam,
        limit: RUNS_PAGE_SIZE,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.has_more ? lastPage.pagination.next_ts : undefined,
    enabled: !!flowScheduleId,
    meta: { persist: false },
    refetchInterval: (query) =>
      query.state.data?.pages[0]?.data.runs.some((r) => r.status === "running")
        ? RUNS_ACTIVE_POLL_MS
        : false,
  });
}

/** Manual run (202) — starts immediately, does not advance the cadence. */
export function useRunFlowScheduleNow(
  options?: UseMutationOptions<FlowScheduleRun, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<FlowScheduleRun, AxiosError, string>({
    mutationFn: async (flowScheduleId) => {
      return await orgAPI.flowSchedules.runNow(flowScheduleId);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["flow-schedule-runs", variables],
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}
